<template>
  <div class="TripDisplay" :class="{ 'TripDisplay--borderLess': borderless }">
    <div class="TripDisplay-content">
      <div class="TripDisplay-details">
        <div v-if="summary || showDate" class="TripDisplay-headerRow">
          <TripTypeHeader v-if="summary" :returnTrip />
          <div v-if="showDate" class="TripDisplay-dateContainer">
            <Icon
              v-if="showDateIcon"
              icon="calendar"
              :size="24"
              class="TripDisplay-calendarIcon"
            />

            <p class="TripDisplay-dateText">
              {{ formattedDepartureDate }}
            </p>
          </div>
        </div>

        <div class="TripDisplay-scheduleRow">
          <!-- Departure -->
          <TripDisplayTimeInfo
            :timezone="departureTimezone"
            :location="departureLocation"
            alignment="start"
          >
            <template #time>
              <Time :date="departureTzDate" />
            </template>
          </TripDisplayTimeInfo>

          <TripDisplayConnector
            :icon="icon"
            :duration="tripDuration"
            :segments="segmentsWithStopDuration"
            :stops
            :connections
          />

          <!-- Arrival -->
          <Tooltip position="top">
            <template #trigger>
              <TripDisplayTimeInfo
                :timezone="arrivalTimezone"
                :location="arrivalLocation"
                :dayOffset
                alignment="end"
              >
                <template #time>
                  <Time :date="arrivalTzDate" />
                </template>
              </TripDisplayTimeInfo>
            </template>
            <p>{{ t('ui.booking.availability.results.destination.date', { date: formattedArrivalDate }) }}</p>
          </Tooltip>
        </div>

        <div v-if="showTripData" class="TripDisplay-additionalInfo">
          <p v-if="carrier" class="TripDisplay-carrier">
            {{ t('ui.booking.availability.results.operatedBy', { carrierName: carrier }) }}
          </p>

          <p v-if="serviceNumbers" class="TripDisplay-serviceNumber">
            {{ serviceNumbers }}
          </p>
        </div>
      </div>
      <!-- Interactions Section. Will have more sub components later. -->
      <div v-if="edit || price" class="TripDisplay-interaction">
        <TripDisplayEdit
          v-if="edit"
          :disabled
          @edit="emit('edit')"
        />

        <TripDisplayAction
          v-else-if="price"
          :disabled
          :price
          :totalPrice
          :seatsLeft
          :loading
          :multiPassengers
          @select="emit('select')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { intervalToDuration } from 'date-fns';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Time from '@cvo/ui/components/atoms/Time/Time.vue';
import Tooltip from '@cvo/ui/components/atoms/Tooltip/Tooltip.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import type { TripSegmentWithStopDuration, TripDisplayProps, TripDisplayEmits } from './TripDisplay.model';
import TripDisplayAction from './TripDisplayAction/TripDisplayAction.vue';
import TripDisplayEdit from './TripDisplayEdit/TripDisplayEdit.vue';
import TripDisplayConnector from './TripDisplayConnector/TripDisplayConnector.vue';
import TripDisplayTimeInfo from './TripDisplayTimeInfo/TripDisplayTimeInfo.vue';
import TripTypeHeader from './TripTypeHeader/TripTypeHeader.vue';
import { useTimezoneDate } from './useTimezoneDates';

const props = withDefaults(defineProps<TripDisplayProps>(), {
  segments: () => [],
});

const emit = defineEmits<TripDisplayEmits>();
const { t } = useI18n();

const {
  tzDate: departureTzDate,
  stationLocalDate: formattedDepartureDate,
  stationTimezoneName: departureTimezone,
} = useTimezoneDate(() => props.departureDate, () => props.departureTimezone);

const {
  tzDate: arrivalTzDate,
  stationLocalDate: formattedArrivalDate,
  stationTimezoneName: arrivalTimezone,
} = useTimezoneDate(() => props.arrivalDate, () => props.arrivalTimezone);

const tripDuration = computed(() => {
  return intervalToDuration({
    start: props.departureDate,
    end: props.arrivalDate,
  });
});

const serviceNumbers = computed(() => {
  const numbers = props.segments.map(segment => segment.serviceNumber).filter(Boolean);
  return numbers.join(' | ');
});

const segmentsWithStopDuration = computed(() =>
  props.segments.map((connection, index): TripSegmentWithStopDuration => {
    const previousConnection = props.segments[index];
    const stopDuration = intervalToDuration({
      start: new Date(connection.departure.date.utc),
      end: new Date(previousConnection.arrival.date.utc),
    });

    return {
      ...connection,
      stopDuration,
    };
  }));

/**
 * Number of days difference between arrival and departure local dates
 */
const dayOffset = computed<number | null>(() => {
  return arrivalTzDate.value.getDate() - departureTzDate.value.getDate();
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

// TODO: Update variables according to the new tokens coming from Figma
// https://caravelo.slack.com/archives/C09FC587FLL/p1775730966423579

@layer cvo-ui {
  .TripDisplay {
    border-radius: vars.$organisms-tripdisplay-lg-md-border-radius;
    background-color: vars.$organisms-tripdisplay-bg-color;
    border: vars.$organisms-tripdisplay-border-stroke solid vars.$organisms-tripdisplay-border-color;
    overflow: hidden;

    @container(max-width: #{vars.$screen-sm-max-width}) {
      border-radius: vars.$organisms-tripdisplay-sm-border-radius;
    }

    &-content {
      display: flex;
      flex-direction: row;
      align-items: stretch;

      @container(max-width: #{vars.$screen-sm-max-width}) {
        flex-direction: column;
      }
    }

    &-details {
      flex: 1;
      padding: vars.$organisms-tripdisplay-lg-md-padding-top-bottom vars.$organisms-tripdisplay-lg-md-padding-left-right;
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
      justify-content: center;

      @container(max-width: #{vars.$screen-sm-max-width}) {
        padding: vars.$organisms-tripdisplay-sm-padding-top-bottom vars.$organisms-tripdisplay-sm-padding-left-right;
        gap: vars.$dimensions-d-3;
      }

        .TripDisplay-headerRow {
        display: flex;
        align-items: center;
        gap: vars.$dimensions-d-4;
        justify-content: space-between;

        @container(max-width: #{vars.$screen-sm-max-width}) {
          flex-direction: column;
          align-items: start;
          gap: vars.$dimensions-d-2;
        }

        .TripDisplay-dateContainer {
          display: flex;
          align-items: center;
          gap: vars.$dimensions-d-2;

          .TripDisplay-calendarIcon {
            color: vars.$organisms-tripdisplay-segmentdetails-direct-default-icon-primary-color;
            flex-shrink: 0;
          }

          .TripDisplay-dateText {
            color: vars.$organisms-tripdisplay-segmentdetails-direct-default-text-time-color;
            @include text.text(md, $font-weight: vars.$typography-weight-strong);

            @container(max-width: #{vars.$screen-sm-max-width}) {
              @include text.text(sm, $font-weight: vars.$typography-weight-strong);
            }
          }
        }
      }

      .TripDisplay-scheduleRow {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: vars.$dimensions-d-5;
        flex-wrap: wrap;

        @container(max-width: #{vars.$screen-sm-max-width}) {
          gap: vars.$dimensions-d-3;
        }
      }

      .TripDisplay-additionalInfo {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;

        @container(max-width: #{vars.$screen-sm-max-width}) {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        }

        .TripDisplay-serviceNumber, .TripDisplay-carrier {
          color: vars.$organisms-tripdisplay-text-secondary-color;
          @include text.text(xs);
        }
      }
    }

    &-interaction {
      width: 204px;
      padding: vars.$organisms-tripdisplay-lg-md-padding-top-bottom vars.$organisms-tripdisplay-lg-md-padding-left-right;
      border-inline-start: vars.$organisms-tripdisplay-border-stroke solid vars.$organisms-tripdisplay-border-color;

      > * {
        height: 100%;
      }

      @container(max-width: #{vars.$screen-sm-max-width}) {
        width: 100%;
        padding: vars.$organisms-tripdisplay-sm-padding-top-bottom vars.$organisms-tripdisplay-sm-padding-left-right;
        border-inline-start: none;
        border-block-start: vars.$organisms-tripdisplay-border-stroke solid vars.$organisms-tripdisplay-border-color;
      }
    }
  }

  .TripDisplay--borderLess {
    border: none;

    .TripDisplay-details {
      padding: 0;
    }
  }
}
</style>